import { useHistory } from 'react-router-dom';
import { Email, Chat } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { URLPaths } from 'models/enums';
import Button from 'domains/core/components/Button';
import PageLayout from 'domains/core/components/PageLayout';
import BrandLearningStrengthCard from './BrandLearningStrengthCard';
import BrandAITrainedBanner from './BrandAITrainedBanner';
import FieldSection from './FieldSection';
import { BrandProfileField, BrandProfileFieldName } from 'domains/brand/types';
import useContentWithFolders from 'hooks/queries/useContentWithFolders';
import { ContentTypeForComparison } from 'domains/content/types';

type Props = {
    brandProfile: BrandProfileField[];
};

const getFieldValue = (field: BrandProfileField): string | string[] => {
    const v = field.currentValue;
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) return v.map(String);
    return '';
};

type ContentItem = { id: number; name: string; type?: string };

const BrandSetupLandingPage = ({ brandProfile }: Props) => {
    const history = useHistory();
    const { data: contentData = [] } = useContentWithFolders({
        folderId: -1,
        statuses: ['published', 'live'],
    });
    const contentItems = contentData.filter((item: { type?: string }) => item.type !== ContentTypeForComparison.FOLDER);

    const getFieldByName = (name: BrandProfileFieldName) => brandProfile.find((f) => f.fieldName === name);
    const getValue = (name: BrandProfileFieldName) => getFieldValue(getFieldByName(name) ?? ({} as BrandProfileField));

    const orgName = getValue(BrandProfileFieldName.OrganizationName) as string;
    const brandDescription = getValue(BrandProfileFieldName.BrandDescription) as string;
    const brandToneKeywords = getValue(BrandProfileFieldName.BrandToneKeywords) as string[];
    const useKeywords = getValue(BrandProfileFieldName.UseKeywords) as string[];
    const dontUseKeywords = getValue(BrandProfileFieldName.DontUseKeywords) as string[];
    const sampleTemplateIds = (getFieldByName(BrandProfileFieldName.SampleTemplateIds)?.currentValue as number[]) ?? [];

    const sampleContent = sampleTemplateIds
        .map((id) => contentItems.find((c: ContentItem) => c.id === id))
        .filter(Boolean) as ContentItem[];

    const handleEdit = () => {
        history.push(URLPaths.SETTINGS_BRAND_EDIT);
    };

    const getContentIcon = (item: ContentItem) => {
        const type = (item.type ?? '').toLowerCase();
        if (type.includes('sms') || type.includes('whatsapp')) return <Chat fontSize="small" />;
        return <Email fontSize="small" />;
    };

    return (
        <Stack data-testid="brand-setup-landing-page" role="main" aria-label="Brand Setup - Landing">
            <PageLayout
                showSettingsHeader
                header="brand setup"
                headerAction={
                    <Button variant="outlined" onClick={handleEdit} data-testid="brand-setup-edit-button">
                        Edit
                    </Button>
                }
            >
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={4}
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    <Stack spacing={3} flex={1} maxWidth={600}>
                        <FieldSection label="Organization name" value={orgName} />
                        <FieldSection label="Brand description" value={brandDescription} />
                        <FieldSection
                            label="Brand tone keywords"
                            value={Array.isArray(brandToneKeywords) ? brandToneKeywords : []}
                            renderAsTags
                        />
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={400} color="text.primary">
                                Sample content
                            </Typography>
                            {sampleContent.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {sampleContent.map((item) => (
                                        <Stack
                                            key={item.id}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                backgroundColor: 'grey.100',
                                                borderRadius: 1,
                                                width: 'fit-content',
                                            }}
                                        >
                                            {getContentIcon(item)}
                                            <Typography variant="body2">{item.name}</Typography>
                                        </Stack>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.primary">
                                    -
                                </Typography>
                            )}
                        </Stack>
                        <FieldSection
                            label="Words and phrases to use"
                            value={Array.isArray(useKeywords) ? useKeywords.join(', ') : ''}
                        />
                        <FieldSection
                            label="Words and phrases to avoid"
                            value={Array.isArray(dontUseKeywords) ? dontUseKeywords.join(', ') : ''}
                        />
                    </Stack>
                    <Stack spacing={4}>
                        <BrandLearningStrengthCard mode="landing" brandProfile={brandProfile} />
                        <BrandAITrainedBanner />
                    </Stack>
                </Stack>
            </PageLayout>
        </Stack>
    );
};

export default BrandSetupLandingPage;
